import type { BusPayload } from "./messages";

/**
 * Shared logic behind the two endpoint blocks, `<gr-input>` and `<gr-output>`.
 *
 * A playground page has exactly two edges worth making explicit: where the
 * data comes in and where the result goes out. Everything between them is
 * ordinary page code, so these helpers stay small and DOM-free — the
 * elements in components/ own the pixels.
 */

/** Signal name for a device topic, or null for other devices and `$` system topics. */
export function deviceSignal(device: string, topic: string): string | null {
  if (device.length === 0 || !topic.startsWith(`${device}/`)) return null;
  const signal = topic.slice(device.length + 1);
  if (signal.length === 0 || signal.startsWith("$")) return null;
  return signal;
}

/** One-line readout of a payload, rounded and clipped to fit a narrow column. */
export function formatSignalValue(payload: BusPayload, max = 44): string {
  if (payload === null) return "null";
  if (typeof payload === "boolean") return payload ? "on" : "off";
  if (typeof payload === "number") {
    return Number.isFinite(payload) ? String(Math.round(payload * 1000) / 1000) : String(payload);
  }
  const text = typeof payload === "string" ? payload : JSON.stringify(payload);
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** Backing size for a mirror canvas: the source aspect, capped in width. */
export function mirrorSize(
  width: number,
  height: number,
  maxWidth: number,
): { width: number; height: number } {
  const w = Math.max(1, Math.round(width) || 16);
  const h = Math.max(1, Math.round(height) || 9);
  if (w <= maxWidth) return { width: w, height: h };
  return { width: maxWidth, height: Math.max(1, Math.round((h * maxWidth) / w)) };
}

/**
 * Reference counter for "something still needs frames".
 *
 * A stage renders while at least one consumer wants pixels — a visible
 * mirror, a live cast, a USB panel, the stage window itself — so a page
 * left open on the console costs no GPU. `onChange` only fires on the
 * transitions, which is all a render loop cares about.
 */
export class FrameDemand {
  private reasons = new Set<string>();

  constructor(private onChange?: (active: boolean, reasons: string[]) => void) {}

  /** Add or drop one reason; returns whether anything still wants frames. */
  want(reason: string, needed: boolean): boolean {
    const before = this.active;
    if (needed) this.reasons.add(reason);
    else this.reasons.delete(reason);
    if (before !== this.active) this.onChange?.(this.active, this.list);
    return this.active;
  }

  has(reason: string): boolean {
    return this.reasons.has(reason);
  }

  get active(): boolean {
    return this.reasons.size > 0;
  }

  get list(): string[] {
    return [...this.reasons];
  }
}
